import {
  DefaultChatTransport,
  parsePartialJson,
  type HttpChatTransportInitOptions,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';

import { EDITOR_WRITE_TOOL_NAMES } from '@flintmint/core-shared/utils';
import { STREAMED_EDITOR_TOOL_NAMES } from '@flintmint/web-shared/utils/llm-message-utils';
import {
  getAgentRunState,
  getCurrentAgentRunId,
  isAgentRunLoading,
  publishAgentRunEvent,
  shouldIgnoreAgentWriteStart,
} from '../../run/agent-run-store';
import { beginStreamingSession, publishStreamingToolEvent, type StreamingToolEvent } from '../streaming-preview';

type PublishStreamingEvent = (requestId: string, event: StreamingToolEvent) => void;
type PublishAgentWriteStarted = (requestId: string, runId: number, toolCallId: string) => void;
type PublishToolProgress = (requestId: string, runId: number, toolName: string) => void;

type PartialToolCall = {
  text: string;
  toolName: string;
};

export const createStreamingChunkProcessor = ({
  requestId,
  runId,
  publishEvent = publishStreamingToolEvent,
  publishAgentWriteStarted = (nextRequestId, nextRunId) => {
    if (!nextRunId || shouldIgnoreAgentWriteStart(nextRequestId)) return;
    beginStreamingSession(nextRequestId, nextRunId);
    publishAgentRunEvent(nextRequestId, nextRunId, { type: 'agent_write_started' });
  },
  // Step-progress signal that used to live in a React effect over `messages`. The old effect only
  // published while the run was loading (`isActive`); the loading guard replicates that gate here, and
  // publishAgentRunEvent drops stale runIds. The reducer still filters non-progress tools, locked and
  // awaiting-summary states, and duplicate steps.
  publishToolProgress = (nextRequestId, nextRunId, toolName) => {
    if (!nextRunId || !isAgentRunLoading(getAgentRunState(nextRequestId))) return;
    publishAgentRunEvent(nextRequestId, nextRunId, { type: 'tool_progressed', toolType: toolName });
  },
}: {
  requestId: string;
  runId: number;
  publishEvent?: PublishStreamingEvent;
  publishAgentWriteStarted?: PublishAgentWriteStarted;
  publishToolProgress?: PublishToolProgress;
}) => {
  const partialToolCalls = new Map<string, PartialToolCall>();
  const startedWriteToolCalls = new Set<string>();
  const progressedToolCalls = new Set<string>();

  const publishParsedEvent = (event: StreamingToolEvent) => {
    if (!STREAMED_EDITOR_TOOL_NAMES.has(event.toolName)) return;
    publishEvent(requestId, event);
  };

  const ensureWriteStarted = (toolName: string, toolCallId: string) => {
    if (!EDITOR_WRITE_TOOL_NAMES.has(toolName) || startedWriteToolCalls.has(toolCallId)) return;
    startedWriteToolCalls.add(toolCallId);
    publishAgentWriteStarted(requestId, runId, toolCallId);
  };

  // Publish once per tool call (on the first start or a start-less final). The reducer dedupes same-step
  // repeats, so start-only matches the old effect that re-fired on every message update.
  const ensureToolProgress = (toolName: string, toolCallId: string) => {
    if (progressedToolCalls.has(toolCallId)) return;
    progressedToolCalls.add(toolCallId);
    publishToolProgress(requestId, runId, toolName);
  };

  const reset = () => {
    partialToolCalls.clear();
    startedWriteToolCalls.clear();
    progressedToolCalls.clear();
  };

  const processChunk = async (chunk: UIMessageChunk): Promise<void> => {
    switch (chunk.type) {
      case 'tool-input-start':
        ensureWriteStarted(chunk.toolName, chunk.toolCallId);
        ensureToolProgress(chunk.toolName, chunk.toolCallId);
        partialToolCalls.set(chunk.toolCallId, {
          text: '',
          toolName: chunk.toolName,
        });
        break;

      case 'tool-input-delta': {
        const partialToolCall = partialToolCalls.get(chunk.toolCallId);
        if (!partialToolCall) return;

        partialToolCall.text += chunk.inputTextDelta;
        const { value } = await parsePartialJson(partialToolCall.text);
        if (value === undefined) return;

        publishParsedEvent({
          runId,
          toolCallId: chunk.toolCallId,
          toolName: partialToolCall.toolName,
          input: value,
          isFinal: false,
        });
        break;
      }

      case 'tool-input-available':
        ensureWriteStarted(chunk.toolName, chunk.toolCallId);
        ensureToolProgress(chunk.toolName, chunk.toolCallId);
        partialToolCalls.delete(chunk.toolCallId);
        publishParsedEvent({
          runId,
          toolCallId: chunk.toolCallId,
          toolName: chunk.toolName,
          input: chunk.input,
          isFinal: true,
        });
        break;

      case 'tool-input-error':
      case 'tool-output-available':
      case 'tool-output-error':
        partialToolCalls.delete(chunk.toolCallId);
        startedWriteToolCalls.delete(chunk.toolCallId);
        progressedToolCalls.delete(chunk.toolCallId);
        break;

      case 'finish':
      case 'abort':
        reset();
        break;
    }
  };

  return {
    processChunk,
  };
};

export class StreamingChatTransport<UI_MESSAGE extends UIMessage> extends DefaultChatTransport<UI_MESSAGE> {
  private readonly requestId: string;

  constructor({
    requestId,
    ...options
  }: HttpChatTransportInitOptions<UI_MESSAGE> & {
    requestId: string;
  }) {
    super(options);
    this.requestId = requestId;
  }

  protected processResponseStream(stream: ReadableStream<Uint8Array<ArrayBufferLike>>): ReadableStream<UIMessageChunk> {
    const chunkProcessor = createStreamingChunkProcessor({
      requestId: this.requestId,
      runId: getCurrentAgentRunId(this.requestId),
    });
    const processedStream = super.processResponseStream(stream);
    const [chatStream, streamingTap] = processedStream.tee();

    void this.consumeStreamingTap(streamingTap, chunkProcessor);

    return chatStream;
  }

  private async consumeStreamingTap(
    stream: ReadableStream<UIMessageChunk>,
    chunkProcessor: ReturnType<typeof createStreamingChunkProcessor>
  ): Promise<void> {
    const reader = stream.getReader();

    try {
      while (true) {
        // A read error also errors the chat branch, which handles it; see use-llm-agent README.
        const result = await reader.read().catch(() => undefined);
        if (!result || result.done) break;
        await chunkProcessor.processChunk(result.value);
      }
    } catch (error) {
      console.error('Failed to process chat streaming chunks', error);
    } finally {
      reader.releaseLock();
    }
  }
}
